<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling-ownership.UI.2 / LINE.3 — the status menu's Calls section:
// "You hold the line for <inbox>" (new calls ring you first) with "Pass the
// line…", and, for the designated person whose line a teammate holds,
// "<name> holds the line for <inbox>" with "Take the line back". The Pass the
// line dialog lives in the app-root call layer (KaptionPassLineDialog) so it
// survives the dropdown closing; this section opens it over the bus.
import { computed } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { DropdownItem, DropdownSeparator } from 'next/dropdown-menu/base';
import { useKaptionCallStore } from './kaptionCallStore';
import { OPEN_PASS_LINE_EVENT } from './kaptionCallEvents';

const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const calls = useKaptionCallStore();

const isEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING')
);
const held = computed(() => calls.heldLines.value);
const passed = computed(() => calls.passedLines.value);
const isVisible = computed(
  () => isEnabled.value && (held.value.length > 0 || passed.value.length > 0)
);

const holderName = line => line.lineHolder?.name || line.lineHolderName || '';

const openPassLine = line =>
  emitter.emit(OPEN_PASS_LINE_EVENT, {
    inboxId: line.inboxId,
    inboxName: line.inboxName,
  });
</script>

<template>
  <template v-if="isVisible">
    <div data-testid="kaption-call-line-menu" class="contents">
      <li class="px-2 pt-1 text-xs font-medium text-n-slate-11">
        {{ $t('KAPTION_CALLS.LINE.SECTION') }}
      </li>
      <template v-for="line in held" :key="`held-${line.inboxId}`">
        <li
          data-testid="kaption-call-line-held"
          class="mx-1 flex items-center gap-2 rounded-lg bg-n-teal-3 px-2 py-1.5"
        >
          <span class="i-lucide-phone-incoming size-4 text-n-teal-11" />
          <span class="flex min-w-0 flex-col">
            <span class="truncate text-sm text-n-slate-12">
              {{ $t('KAPTION_CALLS.LINE.YOU_HOLD', { inbox: line.inboxName }) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('KAPTION_CALLS.LINE.YOU_HOLD_SUB') }}
            </span>
          </span>
        </li>
        <DropdownItem
          data-testid="kaption-call-line-pass"
          icon="i-lucide-arrow-right-left"
          :label="$t('KAPTION_CALLS.LINE.PASS')"
          :click="() => openPassLine(line)"
        />
        <DropdownItem
          v-if="line.designatedUserId !== line.holderUserId"
          data-testid="kaption-call-line-return"
          icon="i-lucide-undo-2"
          :label="$t('KAPTION_CALLS.LINE.GIVE_BACK')"
          :click="() => calls.takeLineBack(line.inboxId)"
        />
      </template>
      <template v-for="line in passed" :key="`passed-${line.inboxId}`">
        <li
          data-testid="kaption-call-line-passed"
          class="mx-1 flex items-center gap-2 px-2 py-1.5"
        >
          <span class="i-lucide-phone-forwarded size-4 text-n-slate-11" />
          <span class="truncate text-sm text-n-slate-12">
            {{
              holderName(line)
                ? $t('KAPTION_CALLS.LINE.HOLDER_HOLDS', {
                    name: holderName(line),
                    inbox: line.inboxName,
                  })
                : $t('KAPTION_CALLS.LINE.SOMEONE_HOLDS', {
                    inbox: line.inboxName,
                  })
            }}
          </span>
        </li>
        <DropdownItem
          data-testid="kaption-call-line-take-back"
          icon="i-lucide-undo-2"
          :label="$t('KAPTION_CALLS.LINE.TAKE_BACK')"
          :click="() => calls.takeLineBack(line.inboxId)"
        />
      </template>
      <DropdownSeparator />
    </div>
  </template>
</template>
